import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';

export default function SolutionsVideoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full pt-20 md:pt-24 pb-6 md:pb-8 bg-background-100/50 overflow-hidden"
    >
      <FloatingShapes count={14} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={10} color="secondary" speed={0.5} />
      </div>
      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Heading */}
          <div
            className={`text-center mb-6 md:mb-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary-100/80 rounded-full w-fit mx-auto mb-5">
              <span className="w-1.5 h-1.5 bg-secondary-500 rounded-full animate-pulse" />
              <span className="text-xs font-mono font-medium text-secondary-700 uppercase tracking-wider">
                {t('solutions.video.badge')}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-foreground-950 tracking-tight mb-4">
              {t('solutions.video.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {t('solutions.video.subtitle')}
            </p>
          </div>

          {/* Video */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative rounded-lg overflow-hidden border border-background-200/60 bg-background-50">
              {/* Glow behind video */}
              <div className="absolute inset-0 -m-6 rounded-3xl bg-accent-500/5 blur-2xl pointer-events-none" />
              <video
                className="relative w-full aspect-video object-cover"
                controls
                playsInline
                preload="metadata"
                poster="https://readdy.ai/api/search-image?query=Clean%20modern%20technology%20dashboard%20showing%20unified%20data%20streams%20converging%20into%20a%20single%20glowing%20node%2C%20deep%20violet%20purple%20and%20soft%20emerald%20mint%20gradient%20background%2C%20refined%20minimal%20enterprise%20aesthetic%2C%20elegant%20and%20precise%2C%20cinematic%20still%20frame&width=1600&height=900&seq=solutions-video-poster-01&orientation=landscape&nocache=true"
              >
                <source
                  key={isEnglish ? 'en' : 'es'}
                  src={
                    isEnglish
                      ? 'https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/3eaf31e3-9e0e-47fc-9c59-52eb40b5f3ff_video-4-ing.mp4'
                      : 'https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/a066091d-ede7-4421-a7b6-82c86006fa78_Video-4-es.mp4'
                  }
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}